import { useReducedMotion } from '#pplx/frontend/common/hooks/useReducedMotion.js';
import { COMPOSER_MOTION_SPRING } from '#pplx/frontend/composer-sdk/composerMotion.js';
import { useTransition } from '@react-spring/web';
import { useEffect, useMemo, useRef } from 'react';
const placeholderAnimationStyles = {
    fade: {
        from: { opacity: 0 },
        enter: { opacity: 1 },
        leave: { opacity: 0 },
    },
    blur: {
        from: { opacity: 0, filter: 'blur(6px)', transform: 'scale(0.98)' },
        enter: { opacity: 1, filter: 'blur(0px)', transform: 'scale(1)' },
        leave: { opacity: 0, filter: 'blur(6px)', transform: 'scale(1.02)' },
    },
    slide: {
        from: { opacity: 0, transform: 'translateY(6px)' },
        enter: { opacity: 1, transform: 'translateY(0px)' },
        leave: { opacity: 0, transform: 'translateY(-6px)' },
    },
};
const getPlaceholderAnimationStyles = (animation) => placeholderAnimationStyles[animation];
export function useComposerPlaceholderAnimation({ text, animation, isRotating, }) {
    const reducedMotion = useReducedMotion();
    const isFirstPlaceholderRender = useRef(true);
    const previousPlaceholderItemRef = useRef(null);
    const currentPlaceholderItem = useMemo(() => ({
        text,
        animation,
    }), [text, animation]);
    const shouldAnimatePlaceholder = isRotating || animation === 'fade' || animation === 'blur';
    const previousPlaceholderItem = previousPlaceholderItemRef.current;
    const shouldImmediatelyEnterPlaceholder = (item) => item.animation === 'fade' && previousPlaceholderItem?.animation === 'fade';
    useEffect(() => {
        isFirstPlaceholderRender.current = false;
    }, []);
    useEffect(() => {
        previousPlaceholderItemRef.current = currentPlaceholderItem;
    }, [currentPlaceholderItem]);
    const immediate = reducedMotion || !shouldAnimatePlaceholder || isFirstPlaceholderRender.current;
    const placeholderTransitions = useTransition(currentPlaceholderItem, {
        keys: (item) => `${item.animation}:${item.text}`,
        from: (item) => ({
            ...getPlaceholderAnimationStyles(item.animation).from,
            immediate: immediate || shouldImmediatelyEnterPlaceholder(item),
        }),
        enter: (item) => ({
            ...getPlaceholderAnimationStyles(item.animation).enter,
            immediate: immediate || shouldImmediatelyEnterPlaceholder(item),
        }),
        leave: (item) => ({
            ...getPlaceholderAnimationStyles(item.animation).leave,
            immediate: immediate || item.animation === 'fade',
        }),
        config: animation === 'blur' ? COMPOSER_MOTION_SPRING : { tension: 260, friction: 28 },
        immediate,
    });
    return { transitions: placeholderTransitions, shouldAnimatePlaceholder };
}
//# sourceMappingURL=useComposerPlaceholderAnimation.js.map